import { BaseFirestore } from "./Firestore";
import { Card, Timer } from "./Card";
import { CustomerUpdate } from "./CustomerUpdate";

export const ActivityType = {
  CONTAINER_MOVEMENT: "container_movement",
  NOTE_ADDED: "note_added",
  NOTE_UPDATED: "note_updated",
  NOTE_DELETED: "note_deleted",
  TIMER_STARTED: "timer_started",
  TIMER_PAUSED: "timer_paused",
  TIMER_RESET: "timer_reset",
  TIMER_RESTARTED: "timer_restarted",
  // A running timer whose countdown was restarted in place — same state, same
  // limit, only the clock moved. Deliberately distinct from TIMER_RESET, which
  // means the timer was stopped (see trackTimerActivities in onCardWrite).
  TIMER_RESET_WHILE_RUNNING: "timer_reset_while_running",
  TIMER_CHANGED: "timer_changed",
  TAG_ADDED: "tag_added",
  TAG_REMOVED: "tag_removed",
  CUSTOMER_UPDATE_CREATED: "customer_update_created",
  CUSTOMER_UPDATE_SCHEDULED: "customer_update_scheduled",
  CUSTOMER_UPDATE_COMPLETED: "customer_update_completed",
  CUSTOMER_UPDATE_CANCELLED: "customer_update_cancelled",
  CUSTOMER_UPDATE_SNOOZED: "customer_update_snoozed",
  CUSTOMER_UPDATE_SKIPPED: "customer_update_skipped",
} as const;

export type ActivityType = (typeof ActivityType)[keyof typeof ActivityType];

/**
 * Sentinel actor used when a change is made by an automation rather than a
 * human. Written to the card's `updatedBy` (and surfaced as an activity's
 * `modifiedBy`) so the Activity Feed attributes automation-driven changes to
 * the automation instead of whatever editor happened to be on the card last.
 */
export const AUTOMATION_ACTOR_ID = "automation";
export const AUTOMATION_ACTOR_DISPLAY_NAME = "Automation";

type BaseActivity = BaseFirestore &
  Pick<
    Card,
    | "customer"
    | "cardNumber"
    | "vehicle"
    | "keyTag"
    | "primaryField"
    | "secondaryField"
    | "tertiaryField"
    | "source"
  > & {
    cardId: string;
    activityType: ActivityType;
    modifiedBy: string;
    modifiedByDisplayName?: string;
  };

export type ContainerMovementActivity = BaseActivity & {
  activityType: typeof ActivityType.CONTAINER_MOVEMENT;
  fromContainerId?: string | null;
  fromContainerName?: string | null;
  toContainerId?: string | null;
  toContainerName?: string | null;
  removedFromBoard?: boolean;
};

export type NoteActivity = BaseActivity & {
  activityType:
    | typeof ActivityType.NOTE_ADDED
    | typeof ActivityType.NOTE_UPDATED
    | typeof ActivityType.NOTE_DELETED;
  noteId: string;
  noteText?: string;
  previousNoteText?: string;
};

export type TimerActivity = BaseActivity & {
  activityType:
    | typeof ActivityType.TIMER_STARTED
    | typeof ActivityType.TIMER_PAUSED
    | typeof ActivityType.TIMER_RESET
    | typeof ActivityType.TIMER_RESTARTED
    | typeof ActivityType.TIMER_RESET_WHILE_RUNNING
    | typeof ActivityType.TIMER_CHANGED;
  timerState?: Timer["state"];
  previousTimerState?: Timer["state"];
  timerValue?: number;
  previousTimerValue?: number;
  timeLimitInMinutes?: string;
  previousTimeLimitInMinutes?: string;
};

export type TagActivity = BaseActivity & {
  activityType: typeof ActivityType.TAG_ADDED | typeof ActivityType.TAG_REMOVED;
  tagId: string;
  tagLabel: string;
};

export type CustomerUpdateActivity = BaseActivity & {
  activityType:
    | typeof ActivityType.CUSTOMER_UPDATE_CREATED
    | typeof ActivityType.CUSTOMER_UPDATE_SCHEDULED
    | typeof ActivityType.CUSTOMER_UPDATE_COMPLETED
    | typeof ActivityType.CUSTOMER_UPDATE_CANCELLED
    | typeof ActivityType.CUSTOMER_UPDATE_SNOOZED
    | typeof ActivityType.CUSTOMER_UPDATE_SKIPPED;
  customerUpdateId: string;
  customerUpdateStatus?: CustomerUpdate["status"];
  previousCustomerUpdateStatus?: CustomerUpdate["status"];
  updateComments?: string;
};

// Legacy ActivityFeed type for backward compatibility with integrations
export type ActivityFeed = BaseFirestore &
  Pick<
    Card,
    | "customer"
    | "cardNumber"
    | "vehicle"
    | "keyTag"
    | "primaryField"
    | "secondaryField"
    | "tertiaryField"
    | "source"
  > & {
    cardId: string;
    fromContainerId?: string | null;
    fromContainerName?: string | null;
    toContainerId?: string | null;
    toContainerName?: string | null;
    removedFromBoard?: boolean;
    modifiedBy: string;
    // New optional fields for enhanced functionality
    activityType?: ActivityType;
    modifiedByDisplayName?: string;
  };

// Union type for all new activity types (to be used in functions-v2)
export type ActivityFeedUnion =
  | ContainerMovementActivity
  | NoteActivity
  | TimerActivity
  | TagActivity
  | CustomerUpdateActivity;

export type ActivityFeedSearchFields = {
  activityType: ActivityType;
  cardId: string;
  modifiedBy: string;
  modifiedByDisplayName?: string;
  cardNumber?: string | null;
  createdAt?: BaseFirestore["createdAt"];
};
